import { Stack, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, ReferenceLine, LabelList, Tooltip,
} from 'recharts';
import { chart, axisTick, formatMoney, formatShort } from '../../../theme/chart';
import type { MonthlyExpense } from './dashboard.types';
import { previousMonthsAverage } from './homeData';

/**
 * Six months of spending, inside "Ritmo dos gastos": the months before in soft blue, the one in focus in
 * full blue — with its projection stacked on top as a dashed outline while
 * it is still running.
 */
export function MonthlyExpensesChart({ months, currency }: { months: MonthlyExpense[]; currency: string }) {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language;
  const money = (value: number) => formatMoney(value, currency, locale);
  const data = months.map((month, index) => {
    const focus = index === months.length - 1;
    const projectedExtra = month.projected !== undefined ? Math.max(month.projected - month.spent, 0) : 0;
    return {
      label: new Date(`${month.month}-01T12:00:00`).toLocaleDateString(locale, { month: 'short' }).replace('.', ''),
      spent: month.spent,
      projectedExtra,
      total: month.spent + projectedExtra,
      focus,
    };
  });
  const last = months[months.length - 1];
  const live = last?.projected !== undefined;
  const subtitle = live || !last
    ? t('home.currentWithProjection')
    : t('home.monthsThrough', {
        count: months.length,
        month: new Date(`${last.month}-01T12:00:00`).toLocaleDateString(locale, { month: 'long' }),
      });
  const { average: avg, count: avgCount } = previousMonthsAverage(months);

  // The month under the pointer, its projection when it has one, and the
  // average for scale — so the dotted line needs no label on the plot.
  const TooltipContent = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload as (typeof data)[number];
    return (
      <Box sx={{ bgcolor: chart.surface, border: `1px solid ${chart.grid}`, borderRadius: 2, px: 1.5, py: 1, fontSize: 12 }}>
        <Box sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Box>
        <div>{t('home.spent')}: {money(row.spent)}</div>
        {row.projectedExtra > 0 && <div>{t('home.projection')}: {money(row.total)}</div>}
        {avg > 0 && <Box sx={{ color: chart.inkMuted }}>{t('home.average')}: {money(avg)}</Box>}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <ResponsiveContainer width="100%" height={230}>
          <BarChart data={data} margin={{ top: 18, right: 8, left: -12, bottom: 0 }} barCategoryGap="32%">
            <CartesianGrid vertical={false} stroke={chart.grid} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={axisTick} />
            <YAxis tickLine={false} axisLine={false} tick={axisTick} tickFormatter={(value) => formatShort(Number(value), locale)} width={56} />
            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.03)' }} content={<TooltipContent />} />
            {avg > 0 && <ReferenceLine y={avg} stroke={chart.inkSecondary} strokeDasharray="2 3" />}
            <Bar dataKey="spent" stackId="m" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {data.map((entry) => (
                <Cell key={entry.label} fill={entry.focus ? chart.primary : chart.primarySoft} />
              ))}
            </Bar>
            <Bar dataKey="projectedExtra" stackId="m" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {data.map((entry) => (
                <Cell key={entry.label} fill="transparent" stroke={chart.primary} strokeDasharray="3 3" />
              ))}
              <LabelList
                dataKey="total"
                position="top"
                fontSize={10}
                fill={chart.inkSecondary}
                formatter={(value: unknown) => (Number(value) > 0 ? formatShort(Number(value), locale) : '')}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {avg > 0 && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, fontSize: 12, color: chart.inkSecondary }}>
            <Box component="span" sx={{ width: 18, borderTop: `2px dotted ${chart.inkSecondary}` }} />
            <span>{t('home.avgPrevMonths', { count: avgCount })} · {money(avg)}</span>
          </Stack>
        )}
        {avg === 0 && <Box component="span" sx={{ fontSize: 13, color: chart.inkMuted }}>{subtitle}</Box>}
    </Box>
  );
}
